import { Plus } from "lucide-react";
import "@app/App.css";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Button } from "@comps/ui/button";
import { post } from "@utils/utils";
import { useState } from "react";
import { useModelStore } from "src/store";
import { useTranslation } from "react-i18next";
import { ObjectInfos } from "@dto/objects";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { getWsButtonStyle, TW_ErrorMessage, TW_NewViewButton, TW_NewViewButtonIcon, TW_NewViewButtonText, useHover } from "@utils/twClasses";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { translate } from "src/i18n/i18n";
import { View } from "@dto/views";

interface NewViewButtonProps {
  objectInfos: ObjectInfos;
  onNewView: (view: View) => void;
}

function NewViewButton({ objectInfos, onNewView }: NewViewButtonProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsColor = useModelStore(state => state.wsColor);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent', null), getWsButtonStyle(wsHoverColor, wsColor))

  const viewAdded = (view: View) => {
    onNewView(view);
    setIsDialogOpen(false);
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_NewViewButton} {...buttonHover}>
        <Plus className={TW_NewViewButtonIcon} />
        <p className={TW_NewViewButtonText}>{t("view.button.new.text")}</p>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("view.dialog.new.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("view.dialog.new.description")}</DialogDescription>
        </DialogHeader>
        <NewViewForm objectInfos={objectInfos} onNewView={viewAdded} />
      </DialogContent>
    </Dialog>
  )
}

export function NewViewForm({ objectInfos, onNewView }: { objectInfos: ObjectInfos, onNewView: (view: View) => void }) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [error, setError] = useState<string | null>(null);

  const formSchema = z.object({
    viewName: z.string().min(1, {
      message: t("view.dialog.error.name.missing"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      viewName: t("view.dialog.name.default.value"),
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!workspace) {
      console.error("No workspace selected");
      return;
    }
    let order = 0;
    if (objectInfos.views && objectInfos.views.length > 0) {
      const lastView = objectInfos.views[objectInfos.views.length - 1];
      order = (lastView.order ?? 0) + DEFAULT_ORDER_INCREMENT;
    }

    const body = {
      name: values.viewName,
      order: order,
    }
    post<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${objectInfos.object._key}/views`, body).then(newView => {
      if (newView) {
        if (isCompozzError(newView)) {
          const cError = newView as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {          
          setError(null);
          onNewView(newView as View);
        }
      }
    })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="viewName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("view.dialog.name.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("view.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className="ButtonList">
          <Button className="AppDialogButton" type="submit">{t("general.button.ok.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default NewViewButton;
